import { styled } from "@mui/material/styles";
import { colors } from "@zapalacorp/tokens";

/** Wrapper for the grid only; gray styling stays on each `BingoSheetGridCell`. */
export const BingoSheetArea = styled("div")({
  width: "100%",
  marginTop: 24,
  boxSizing: "border-box",
});

export const BingoSheetGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(9, minmax(0, 1fr))",
  gridAutoRows: 44,
  gap: 8,
  width: "100%",
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "repeat(18, minmax(0, 1fr))",
  },
}));

/**
 * Global `button:focus { border: 0 none }` in shipeezi-custom.scss strips borders on native buttons.
 * Re-declare border on :focus / :active so selected vs unselected look the same whether focus is on the
 * cell or on the Item description input (higher specificity: .class:focus beats button:focus).
 */
const cellBorder = ($active: boolean) =>
  `2px solid ${$active ? colors.brandBlue600 : colors.gray500}`;

export const BingoSheetGridCell = styled("button")<{ $active?: boolean }>(({ $active }) => ({
  boxSizing: "border-box",
  minWidth: 0,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 6px",
  border: cellBorder(!!$active),
  backgroundColor: colors.gray100,
  borderRadius: 8,
  cursor: "pointer",
  font: "600 14px/20px Manrope",
  letterSpacing: 0.1,
  color: colors.gray500,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  outline: "none",
  boxShadow: "none",
  WebkitTapHighlightColor: "transparent",
  "&:hover": {
    backgroundColor: colors.gray200,
  },
  "&:focus": {
    border: cellBorder(!!$active),
    outline: "none",
    boxShadow: "none",
  },
  "&:active": {
    border: cellBorder(!!$active),
    outline: "none",
    boxShadow: "none",
  },
  ...(!$active
    ? {
        "&:focus-visible": {
          outline: `2px solid ${colors.brandBlue600}`,
          outlineOffset: 2,
        },
      }
    : {
        "&:focus-visible": {
          outline: "none",
        },
      }),
}));

/** Read-only number cell (no row-level description editing). */
export const BingoSheetGridNumberCell = styled("div")({
  boxSizing: "border-box",
  minWidth: 0,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 6px",
  border: cellBorder(false),
  backgroundColor: colors.gray100,
  borderRadius: 8,
  cursor: "default",
  font: "600 14px/20px Manrope",
  letterSpacing: 0.1,
  color: colors.gray500,
});

export const FormFieldLabel = styled("label")({
  display: "block",
  fontFamily: "Manrope",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "20px",
  marginBottom: 8,
  color: colors.gray700,
  "& .req": {
    color: colors.error500,
  },
});

export const StartingNumberStepperBox = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 4,
  minHeight: 40,
  padding: "4px 8px",
  border: `1px solid ${colors.gray300}`,
  borderRadius: 8,
  backgroundColor: colors.white,
  maxWidth: "100%",
});

export const StartingNumberStepperInput = styled("input")({
  flex: 1,
  minWidth: 0,
  textAlign: "center",
  fontFamily: "Manrope",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: colors.gray700,
  border: "none",
  background: "transparent",
  padding: "4px 8px",
  outline: "none",
  "&:focus-visible": {
    outline: `2px solid ${colors.brandBlue600}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
});

export const ContentTitle = styled("span")({
  fontFamily: "Manrope",
  fontSize: 18,
  fontWeight: 700,
  lineHeight: "27px",
});

export const PaginationWrapper = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 0",
});

export const PaginationDisplayQuantityWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

export const PaginationDisplayQuantity = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

export const PaginationButtons = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

/** Grouped control: prev | current page | next */
export const PaginationNavCluster = styled("div")({
  display: "flex",
  alignItems: "stretch",
  "& > *": {
    border: `1px solid ${colors.gray200}`,
    marginLeft: -1,
    boxSizing: "border-box",
  },
  "& > *:first-of-type": {
    marginLeft: 0,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  "& > *:last-of-type": {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
});

export const PaginationNavIconButton = styled("button")({
  backgroundColor: "transparent",
  cursor: "pointer",
  padding: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:focus-visible": {
    outline: `2px solid ${colors.brandBlue600}`,
    outlineOffset: 2,
  },
  "&:disabled": {
    opacity: 0.8,
    cursor: "not-allowed",
  },
});

export const PaginationCurrentPageDisplay = styled("span")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 44,
  padding: "10px 14px",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  letterSpacing: 0.1,
  color: colors.brandBlue600,
  fontFamily: "Manrope",
  backgroundColor: colors.white,
});
